import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort, Sort} from '@angular/material/sort';
import {BrokerService} from '../../../services/broker.service';
import {TableControl} from '../../../../common/models/TableControl';
import {SharedService} from '../../../../common/services/shared-service';
import {BrokerDTO} from '../../../models/broker/BrokerDTO';
import {OtherRequestBuilder} from '../../../../other/services/other-request-builder';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {DateUtil} from '../../../../common/util/DateUtil';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {detailsExpand} from '../../../../common';
import {DatePipe} from '@angular/common';
import {getSortDirection} from '../../../../common/util/Util';
import {MatIconType} from '../../../../common/types/icon.enum';
import {PortalType} from '../../../../common/types/portal.enum';

enum TableColumns {
  Icons = 'icons',
  BrokerId = 'brokerId',
  BrokerNumber = 'brokerNumber',
  Name = 'name',
  BrokerGroup = 'brokerGroup',
  BusinessType = 'businessType',
  EntityStatus = 'entityStatus',
  StartDate = 'startDate',
}

@Component({
  selector: 'greydom-brokers',
  templateUrl: './brokers.component.html',
  styleUrls: [],
  animations: [detailsExpand],
  providers: [DatePipe] // Add DatePipe to providers
})
export class BrokersComponent implements OnInit, OnDestroy {
  readonly tableColumns = TableColumns;
  readonly destroyed$: Subject<boolean> = new Subject();
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly dateFormat = DateUtil.dateFormat;
  readonly DisplayIcon = MatIconType;
  readonly PortalType = PortalType;

  displayedColumns: TableColumns[] = Object.values<TableColumns>(TableColumns);
  dataSource: MatTableDataSource<BrokerDTO> = new MatTableDataSource();
  expandedElement: BrokerDTO | null;
  brokerTableControl: TableControl;
  currentPortal: PortalType = PortalType.Normal;

  @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: false}) sort: MatSort;

  constructor(
    private router: Router,
    private brokerService: BrokerService,
    private sharedService: SharedService,
    private activatedRoute: ActivatedRoute
  ) {
    const controls = OtherRequestBuilder.getTableControls();
    this.brokerTableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'brokerId',
      'DESCENDING',
      '',
      ''
    );

    const parentData = this.activatedRoute.parent?.snapshot.data;
    this.currentPortal = parentData?.portalType || PortalType.Normal;
  }

  ngOnInit(): void {
    this.getListOfBrokers(this.brokerTableControl);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onValueChanged(value: string): void {
    this.brokerTableControl.filterValue = value;
    this.brokerTableControl.pageNumber = 0;
    this.getListOfBrokers(this.brokerTableControl);
  }

  pageChanged($event: PageEvent): void {
    this.brokerTableControl.pageNumber = $event.pageIndex;
    this.brokerTableControl.pageSize = $event.pageSize;
    this.getListOfBrokers(this.brokerTableControl);
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    if (this.paginator != null) {
      this.paginator.firstPage();
    }
    this.brokerTableControl.pageNumber = 0;

    const sortFieldMap = {
      [TableColumns.BrokerGroup]: 'brokerGroupByBrokerGroupId.name',
      [TableColumns.BusinessType]: 'businessTypeByBusinessTypeId.name',
      [TableColumns.EntityStatus]: 'entityStatusByEntityStatusId.name'
    };

    this.brokerTableControl.sortField = sortFieldMap[$event.active] || $event.active;
    this.brokerTableControl.sortDirection = getSortDirection($event.direction);
    this.getListOfBrokers(this.brokerTableControl);
  }

  getListOfBrokers(tableControl: TableControl): void {
    const pagedRequestDTO: PagedRequestDTO = this.getPagedRequestDTO(tableControl);

    this.brokerService.getListOfBrokers(pagedRequestDTO)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.resultList;
          this.brokerTableControl.length = response.totalRecords;
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }

  onClickAddBroker(): void {
    this.router.navigate([this.getRouteForAddBroker()]);
  }

  onClickEditBroker(brokerId: number): void {
    this.router.navigate([this.getRouteForEditBroker(brokerId.toString())]);
  }

  private getPagedRequestDTO(tableControl: TableControl): PagedRequestDTO {
    const pagedRequestDTO: PagedRequestDTO = OtherRequestBuilder.getTblBrokerPagedRequestDTO(tableControl);
    pagedRequestDTO.fieldMap = {};

    if (tableControl?.filterValue?.trim()) {
      const value = tableControl.filterValue.trim();
      const fields = [
        {key: 'name', name: 'name', logicGate: 'AND', bracket: '('},
        {key: 'brokerNumber', name: 'brokerNumber', logicGate: 'OR', bracket: ')'}
      ];

      fields.forEach((field) => {
        pagedRequestDTO.fieldMap[field.key] = {
          name: field.name,
          value,
          logicGate: field.logicGate,
          bracket: field.bracket || ''
        };
      });
    }

    return pagedRequestDTO;
  }

  getRouteForAddBroker(): string {
    const routes = {
      [this.PortalType.Normal]: '/enterprise-management/add-broker',
      [this.PortalType.OnBoarding]: '/onboarding/add-broker'
    };
    return routes[this.currentPortal] || routes[this.PortalType.Normal];
  }

  getRouteForEditBroker(id: string): string {
    const routes = {
      [this.PortalType.Normal]: `/enterprise-management/edit-broker/${id}`,
      [this.PortalType.OnBoarding]: `/onboarding/edit-broker/${id}`
    };
    return routes[this.currentPortal] || routes[this.PortalType.Normal];
  }
}
